import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../api.js";
import { useAuth } from "../../context/AuthContext.jsx";
import { ForumProvider, useForum } from "./ForumContext.jsx";
import "./Forum.css";

function timeSince(date) {
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60)   return "الآن";
  if (diff < 3600) return `${Math.floor(diff / 60)} دقيقة`;
  if (diff < 86400)return `${Math.floor(diff / 3600)} ساعة`;
  return new Date(date).toLocaleDateString("ar-EG");
}

function TypeBadge({ type }) {
  const map   = { discussion: "tbadge-discussion", artwork: "tbadge-artwork", book: "tbadge-book", pdf: "tbadge-pdf", video: "tbadge-video" };
  const label = { discussion: "نقاش", artwork: "أعمال", book: "كتاب", pdf: "PDF", video: "فيديو" };
  return <span className={`tbadge ${map[type] || "tbadge-discussion"}`}>{label[type] || type}</span>;
}

const REACTIONS = ["👍", "❤️", "🔥", "😮", "🎨", "💡"];

function PostCard({ post, onReact, onQuote, onDelete, currentUserId, isMod }) {
  const isOwn = post.authorId === currentUserId;
  const canDelete = isOwn || isMod;

  return (
    <div className="forum-post-card">
      <div className="forum-post-header">
        <img
          className="forum-post-avatar"
          src={post.authorAvatar || ""}
          alt={post.authorName}
          onError={e => { e.target.style.display = "none"; }}
        />
        <div style={{ flex: 1 }}>
          <div className="forum-post-author">{post.authorName}</div>
          <div className="forum-post-date">
            {timeSince(post.createdAt)}
            {post.editedAt && " · (تم التعديل)"}
          </div>
        </div>
        {post.isAdminNote && (
          <span style={{ background: "var(--red-light)", color: "var(--red)", fontSize: "0.72rem", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>
            ملاحظة إدارية
          </span>
        )}
      </div>

      {/* Quote */}
      {post.quotedContent && (
        <div className="forum-post-quote">
          <div className="forum-post-quote-author">↩ {post.quotedAuthorName}</div>
          <div style={{ color: "var(--text-3)" }}>{post.quotedContent?.slice(0, 200)}{post.quotedContent?.length > 200 ? "…" : ""}</div>
        </div>
      )}

      {/* Body */}
      {(post.contentAr || post.contentEn) && (
        <div className="forum-post-body">{post.contentAr || post.contentEn}</div>
      )}

      {/* Image attachments */}
      {post.attachments?.filter(a => a.kind === "image").length > 0 && (
        <div className="forum-attach-images">
          {post.attachments.filter(a => a.kind === "image").map((a, i) => (
            <img key={i} className="forum-attach-img" src={a.url} alt={a.name || "صورة"} onClick={() => window.open(a.url, "_blank")} />
          ))}
        </div>
      )}

      {/* Video attachment */}
      {post.attachments?.filter(a => a.kind === "video").map((a, i) => (
        <div key={i} className="forum-video-wrap" onClick={() => a.url && window.open(a.url, "_blank")}>
          <div className="forum-video-thumb" style={{ background: a.thumbnail ? `url(${a.thumbnail}) center/cover` : "#111" }}>
            <div className="forum-video-play">▶</div>
            {a.name && <div className="forum-video-label">{a.name}</div>}
          </div>
          {(a.duration || a.resolution || a.size) && (
            <div className="forum-video-meta">
              🎬 {[a.duration, a.resolution, a.size ? `${Math.round(a.size / 1024 / 1024)} MB` : null].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      ))}

      {/* PDF attachment */}
      {post.attachments?.filter(a => a.kind === "pdf").map((a, i) => (
        <div key={i} style={{ margin: "0 18px 14px" }}>
          <a href={a.url} target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "var(--green-light)", color: "var(--green)", padding: "8px 14px", borderRadius: "var(--r-xs)", fontWeight: 700, fontSize: "0.85rem", textDecoration: "none" }}>
            📄 {a.name || "تنزيل PDF"}
          </a>
        </div>
      ))}

      {/* Reactions */}
      <div className="forum-post-reactions">
        {REACTIONS.map(emoji => {
          const r = post.reactions?.find(x => x.emoji === emoji);
          const count = r?.count || 0;
          const mine  = r?.users?.includes(currentUserId);
          if (count === 0 && !mine) return null;
          return (
            <button key={emoji} className={`forum-reaction-btn ${mine ? "mine" : ""}`} onClick={() => onReact(post._id, emoji)}>
              {emoji} {count > 0 && count}
            </button>
          );
        })}
        <button className="forum-reaction-btn" style={{ color: "var(--text-3)", border: "1px dashed var(--card-border)" }} onClick={() => {
          const el = document.getElementById(`react-picker-${post._id}`);
          if (el) el.style.display = el.style.display === "none" ? "flex" : "none";
        }}>+ 😊</button>
        <div id={`react-picker-${post._id}`} style={{ display: "none", gap: 6, flexWrap: "wrap" }}>
          {REACTIONS.map(e => (
            <button key={e} className="forum-reaction-btn" onClick={() => {
              onReact(post._id, e);
              document.getElementById(`react-picker-${post._id}`).style.display = "none";
            }}>{e}</button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="forum-post-actions">
        <button className="forum-post-action-btn" onClick={() => onQuote(post)}>↩ اقتبس</button>
        {canDelete && (
          <button className="forum-post-action-btn" style={{ color: "var(--red)" }} onClick={() => onDelete(post._id)}>🗑 حذف</button>
        )}
      </div>
    </div>
  );
}

function ThreadPage() {
  const { id }     = useParams();
  const { token, artist, isLoggedIn } = useAuth();
  const { isMember, isMod } = useForum();

  const [thread, setThread]   = useState(null);
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyType, setReplyType] = useState("text");
  const [quotedPost, setQuotedPost] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [liked, setLiked]     = useState(false);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [copied, setCopied]   = useState(false);

  const headers = () => token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/api/forum/threads/${id}`);
      setThread(data.thread);
      setPosts(data.posts);
      setPages(Math.ceil((data.thread.replyCount + 1) / 30));
    } catch {
      setThread(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadMore = useCallback(async (p) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/forum/posts`, { params: { threadId: id, page: p, limit: 30 } });
      setPosts(data.posts);
      setPages(data.pages);
    } catch {}
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleLike = async () => {
    if (!isMember) return;
    try {
      const { data } = await axios.post(`${API_BASE}/api/forum/threads/${id}/like`, {}, { headers: headers() });
      setLiked(data.liked);
      setThread(t => ({ ...t, likeCount: data.likeCount }));
    } catch {}
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        threadId: id,
        contentAr: replyText.trim(),
        type: replyType,
        quotedPostId: quotedPost?._id,
      };
      const { data } = await axios.post(`${API_BASE}/api/forum/posts`, payload, { headers: headers() });
      setPosts(p => [...p, data]);
      setThread(t => ({ ...t, replyCount: (t.replyCount || 0) + 1 }));
      setReplyText("");
      setQuotedPost(null);
    } catch (err) {
      alert(err.response?.data?.error || "حدث خطأ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReact = async (postId, emoji) => {
    if (!isMember) return;
    try {
      const { data } = await axios.post(`${API_BASE}/api/forum/posts/${postId}/react`, { emoji }, { headers: headers() });
      setPosts(ps => ps.map(p => p._id === postId ? { ...p, reactions: data } : p));
    } catch {}
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("حذف هذه المشاركة؟")) return;
    try {
      await axios.delete(`${API_BASE}/api/forum/posts/${postId}`, { headers: headers() });
      setPosts(ps => ps.filter(p => p._id !== postId));
      setThread(t => ({ ...t, replyCount: Math.max(0, (t.replyCount || 1) - 1) }));
    } catch {}
  };

  if (loading) return <div className="forum-loading">جارٍ التحميل…</div>;
  if (!thread) return <div className="forum-empty">النقاش غير موجود</div>;

  const catSlug = thread.categoryId?.slug;
  const catName = thread.categoryId?.name_ar;

  return (
    <div className="forum-wrap">
      {/* Breadcrumb */}
      <div className="forum-breadcrumb">
        <Link to="/Forum">المنتدى</Link>
        <span className="forum-breadcrumb-sep">›</span>
        {catSlug && <Link to={`/Forum/category/${catSlug}`}>{catName || "القسم"}</Link>}
        {catSlug && <span className="forum-breadcrumb-sep">›</span>}
        <span style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {thread.title_ar || thread.title_en}
        </span>
      </div>

      {/* Thread header */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--card-border)", borderRadius: "var(--r-sm)", padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <TypeBadge type={thread.type} />
          {thread.isPinned && <span className="forum-pin-badge">📌 مثبّت</span>}
          {thread.isLocked && <span className="forum-lock-badge">🔒 مغلق</span>}
        </div>
        <h1 style={{ fontSize: "1.25rem", fontWeight: 800, margin: "0 0 8px" }}>{thread.title_ar || thread.title_en}</h1>
        {thread.title_ar && thread.title_en && (
          <div style={{ fontSize: "0.82rem", color: "var(--text-3)", marginBottom: 12 }}>{thread.title_en}</div>
        )}
        <div style={{ display: "flex", gap: 16, fontSize: "0.78rem", color: "var(--text-3)", flexWrap: "wrap" }}>
          <span>✍️ {thread.authorName}</span>
          <span>👁 {thread.viewCount} مشاهدة</span>
          <span>💬 {thread.replyCount} رد</span>
          <button
            style={{ background: "none", border: "none", color: liked ? "var(--red)" : "var(--text-3)", cursor: "pointer", fontFamily: "Cairo, sans-serif", fontSize: "0.78rem", padding: 0 }}
            onClick={handleLike}
          >
            ❤️ {thread.likeCount}
          </button>
        </div>

        {/* Share bar */}
        {(() => {
          const url  = encodeURIComponent(window.location.href);
          const text = encodeURIComponent(thread.title_ar || thread.title_en);
          const shareBtn = (href, label, bg) => (
            <a href={href} target="_blank" rel="noopener noreferrer" style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "4px 11px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700,
              background: bg, color: "#fff", textDecoration: "none", transition: "opacity 0.15s",
            }}
            onMouseOver={e => e.currentTarget.style.opacity = "0.8"}
            onMouseOut={e => e.currentTarget.style.opacity = "1"}
            >{label}</a>
          );
          return (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14, alignItems: "center" }}>
              <span style={{ fontSize: "0.7rem", color: "var(--text-3)", fontWeight: 700, letterSpacing: "0.08em" }}>شارك:</span>
              {shareBtn(`https://wa.me/?text=${text}%20${url}`,       "WhatsApp",  "#25D366")}
              {shareBtn(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "Facebook", "#1877F2")}
              {shareBtn(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "X / Twitter", "#000")}
              {shareBtn(`https://t.me/share/url?url=${url}&text=${text}`, "Telegram", "#229ED9")}
              <button onClick={() => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 11px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700,
                  background: copied ? "#2e7d32" : "var(--surface-2)", color: copied ? "#fff" : "var(--text-2)",
                  border: "1px solid var(--card-border)", cursor: "pointer", transition: "all 0.2s" }}>
                {copied ? "✓ تم النسخ" : "🔗 نسخ الرابط"}
              </button>
            </div>
          );
        })()}
      </div>

      {/* First post (thread OP) */}
      {(thread.contentAr || thread.contentEn) && (
        <div className="forum-post-card" style={{ borderRight: "3px solid var(--red)" }}>
          <div className="forum-post-header">
            <img className="forum-post-avatar" src={thread.authorAvatar || ""} alt={thread.authorName} onError={e => { e.target.style.display = "none"; }} />
            <div>
              <div className="forum-post-author">{thread.authorName}</div>
              <div className="forum-post-date">{timeSince(thread.createdAt)}</div>
            </div>
          </div>
          <div className="forum-post-body">{thread.contentAr || thread.contentEn}</div>
          {/* Thread images */}
          {thread.attachments?.filter(a => a.kind === "image").length > 0 && (
            <div className="forum-attach-images">
              {thread.attachments.filter(a => a.kind === "image").map((a, i) => (
                <img key={i} className="forum-attach-img" src={a.url} alt={a.name || "صورة"} onClick={() => window.open(a.url, "_blank")} />
              ))}
            </div>
          )}
          {thread.attachments?.filter(a => a.kind === "video").map((a, i) => (
            <div key={i} className="forum-video-wrap" onClick={() => a.url && window.open(a.url, "_blank")}>
              <div className="forum-video-thumb">
                <div className="forum-video-play">▶</div>
                {a.name && <div className="forum-video-label">{a.name}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Replies */}
      {posts.map(post => (
        <PostCard
          key={post._id}
          post={post}
          onReact={handleReact}
          onQuote={setQuotedPost}
          onDelete={handleDelete}
          currentUserId={artist?._id}
          isMod={isMod}
        />
      ))}

      {/* Pagination */}
      {pages > 1 && (
        <div className="forum-pagination">
          {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`forum-page-btn ${p === page ? "active" : ""}`} onClick={() => { setPage(p); loadMore(p); }}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Reply form */}
      {isMember && !thread.isLocked && (
        <form className="forum-reply-form" onSubmit={handleReply}>
          {quotedPost && (
            <div style={{ padding: "8px 14px", background: "var(--bg-2)", borderBottom: "1px solid var(--card-border)", display: "flex", justifyContent: "space-between", fontSize: "0.82rem" }}>
              <span>↩ اقتبس من: <strong>{quotedPost.authorName}</strong></span>
              <button type="button" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)" }} onClick={() => setQuotedPost(null)}>✕</button>
            </div>
          )}
          <div className="forum-reply-toolbar">
            {[["text","💬 نص"],["artwork","🖼 أعمال"],["book","📚 كتاب"],["pdf","📄 PDF"],["video","🎬 فيديو"]].map(([t, l]) => (
              <button key={t} type="button" className={`forum-reply-toolbar-btn ${replyType === t ? "active" : ""}`}
                style={replyType === t ? { background: "var(--red-light)", color: "var(--red)", borderColor: "var(--red)" } : {}}
                onClick={() => setReplyType(t)}>{l}</button>
            ))}
          </div>
          <textarea
            className="forum-reply-textarea"
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="اكتب ردّك هنا…"
          />
          <div className="forum-reply-footer">
            <span style={{ fontSize: "0.78rem", color: "var(--text-3)" }}>
              {replyText.length}/2000
            </span>
            <button type="submit" className="forum-reply-submit" disabled={submitting || !replyText.trim()}>
              {submitting ? "جارٍ الإرسال…" : "إرسال الرد"}
            </button>
          </div>
        </form>
      )}

      {!isLoggedIn && (
        <div className="forum-empty" style={{ marginTop: 24 }}>
          <Link to="/login" style={{ color: "var(--red)", fontWeight: 700 }}>سجّل الدخول</Link> للمشاركة في النقاش
        </div>
      )}
      {isLoggedIn && !isMember && (
        <div className="forum-empty" style={{ marginTop: 24 }}>
          <Link to="/Forum/register" style={{ color: "var(--green)", fontWeight: 700 }}>انضم للمنتدى</Link> للمشاركة في النقاش
        </div>
      )}
      {thread.isLocked && isMember && (
        <div className="forum-empty" style={{ marginTop: 24, color: "var(--text-3)" }}>🔒 هذا النقاش مغلق</div>
      )}
    </div>
  );
}

export default function ForumThread() {
  return (
    <ForumProvider>
      <ThreadPage />
    </ForumProvider>
  );
}
